import { _ as _class_call_check } from "@swc/helpers/_/_class_call_check";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import sumBy from "lodash/sumBy";
import uniq from "lodash/uniq";
import { logAssert } from "./logging";
import { getCentsInLocalCurrency, moneyOrZero } from "./money";
import { isNotUndefined } from "./typescriptUtils";
export var MoneyMath = function MoneyMath() {
    "use strict";
    _class_call_check(this, MoneyMath);
};
MoneyMath.multiply = function(money) {
    var multiplier = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 1;
    var amount_cents = money.amount_cents, currency = money.currency, original_amount_cents = money.original_amount_cents, original_currency = money.original_currency;
    var newAmount;
    var newOriginalAmount;
    if (amount_cents !== undefined) {
        newAmount = amount_cents * multiplier;
    }
    if (original_amount_cents !== undefined) {
        newOriginalAmount = original_amount_cents * multiplier;
    }
    return IFaireMoney.build({
        amount_cents: newAmount,
        currency: currency,
        original_amount_cents: newOriginalAmount,
        original_currency: original_currency
    });
};
MoneyMath.sum = function() {
    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
        args[_key] = arguments[_key];
    }
    var definedMoneys = args.filter(isNotUndefined);
    var uniqCurrencies = uniq(definedMoneys.map(function(money) {
        return money.currency;
    }));
    if (uniqCurrencies.length > 1) {
        throw new Error("Summing money is currently only possible if all currencies are the same. Currencies provided: ".concat(uniqCurrencies));
    }
    var total = sumBy(definedMoneys, function(money) {
        var _money_amount_cents;
        return (_money_amount_cents = money.amount_cents) !== null && _money_amount_cents !== void 0 ? _money_amount_cents : 0;
    });
    return IFaireMoney.build({
        currency: uniqCurrencies[0],
        amount_cents: total
    });
};
MoneyMath.sumAfterConversion = function() {
    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
        args[_key] = arguments[_key];
    }
    var definedMoneys = args.filter(isNotUndefined);
    var uniqCurrencies = uniq(definedMoneys.map(function(money) {
        return money.currency;
    }));
    if (uniqCurrencies.length > 1) {
        throw new Error("Summing money is currently only possible if all currencies are the same. Currencies provided: ".concat(uniqCurrencies));
    }
    var total = sumBy(definedMoneys, function(money) {
        var _getCentsInLocalCurrency;
        return (_getCentsInLocalCurrency = getCentsInLocalCurrency(money)) !== null && _getCentsInLocalCurrency !== void 0 ? _getCentsInLocalCurrency : 0;
    });
    return IFaireMoney.build({
        currency: uniqCurrencies[0],
        amount_cents: total
    });
};
MoneyMath.subtract = function(minuend) {
    for(var _len = arguments.length, subtrahends = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++){
        subtrahends[_key - 1] = arguments[_key];
    }
    var definedSubtrahends = subtrahends.filter(isNotUndefined);
    var uniqSubrahendCurrencies = uniq(definedSubtrahends.map(function(money) {
        return money.currency;
    }));
    if (uniqSubrahendCurrencies.length > 1 || minuend.currency !== uniqSubrahendCurrencies[0]) {
        throw new Error("Subtracting money is currently only possible if the currencies are the same. Minuend currency: ".concat(minuend === null || minuend === void 0 ? void 0 : minuend.currency, ". Subtrahend currencies: ").concat(uniqSubrahendCurrencies));
    }
    var _minuend_amount_cents;
    var result = (_minuend_amount_cents = minuend.amount_cents) !== null && _minuend_amount_cents !== void 0 ? _minuend_amount_cents : 0;
    definedSubtrahends.forEach(function(subtrahend) {
        var _subtrahend_amount_cents;
        result -= (_subtrahend_amount_cents = subtrahend.amount_cents) !== null && _subtrahend_amount_cents !== void 0 ? _subtrahend_amount_cents : 0;
    });
    return IFaireMoney.build({
        amount_cents: result,
        currency: minuend.currency
    });
};
MoneyMath.lessThan = function(a, b) {
    logAssert(a.currency === b.currency, "Comparing IFaireMoney objects of different currencies: ".concat(a.currency, " and ").concat(b.currency));
    logAssert(a.amount_cents !== undefined && b.amount_cents !== undefined, "One or both of the amount_cents to be compared is undefined");
    var _a_amount_cents, _b_amount_cents;
    return ((_a_amount_cents = a.amount_cents) !== null && _a_amount_cents !== void 0 ? _a_amount_cents : 0) < ((_b_amount_cents = b.amount_cents) !== null && _b_amount_cents !== void 0 ? _b_amount_cents : 0);
};
MoneyMath.lessThanZero = function(money) {
    return MoneyMath.lessThan(money, moneyOrZero(undefined, money.currency));
};
MoneyMath.greaterThan = function(a, b) {
    logAssert(a.currency === b.currency, "Comparing IFaireMoney objects of different currencies: ".concat(a.currency, " and ").concat(b.currency));
    logAssert(a.amount_cents !== undefined && b.amount_cents !== undefined, "One or both of the amount_cents to be compared is undefined");
    var _a_amount_cents, _b_amount_cents;
    return ((_a_amount_cents = a.amount_cents) !== null && _a_amount_cents !== void 0 ? _a_amount_cents : 0) > ((_b_amount_cents = b.amount_cents) !== null && _b_amount_cents !== void 0 ? _b_amount_cents : 0);
};
MoneyMath.greaterThanZero = function(money) {
    return MoneyMath.greaterThan(money, moneyOrZero(undefined, money.currency));
};
MoneyMath.isEqual = function(a, b) {
    logAssert(a.amount_cents !== undefined && b.amount_cents !== undefined, "One or both of the amount_cents to be compared is undefined");
    return a.amount_cents === b.amount_cents && a.currency === b.currency;
};
MoneyMath.isEqualToZero = function(money) {
    return MoneyMath.isEqual(money, moneyOrZero(undefined, money.currency));
};
MoneyMath.notEqual = function(a, b) {
    logAssert(a.amount_cents !== undefined && b.amount_cents !== undefined, "One or both of the amount_cents to be compared is undefined");
    return a.amount_cents !== b.amount_cents || a.currency !== b.currency;
};
MoneyMath.notEqualToZero = function(money) {
    return MoneyMath.notEqual(money, moneyOrZero(undefined, money.currency));
};
MoneyMath.lessOrEqual = function(a, b) {
    logAssert(a.currency === b.currency, "Comparing IFaireMoney objects of different currencies: ".concat(a.currency, " and ").concat(b.currency));
    logAssert(a.amount_cents !== undefined && b.amount_cents !== undefined, "One or both of the amount_cents to be compared is undefined");
    var _a_amount_cents, _b_amount_cents;
    return ((_a_amount_cents = a.amount_cents) !== null && _a_amount_cents !== void 0 ? _a_amount_cents : 0) <= ((_b_amount_cents = b.amount_cents) !== null && _b_amount_cents !== void 0 ? _b_amount_cents : 0);
};
MoneyMath.lessOrEqualToZero = function(money) {
    return MoneyMath.lessOrEqual(money, moneyOrZero(undefined, money.currency));
};
MoneyMath.greaterOrEqual = function(a, b) {
    logAssert(a.currency === b.currency, "Comparing IFaireMoney objects of different currencies: ".concat(a.currency, " and ").concat(b.currency));
    logAssert(a.amount_cents !== undefined && b.amount_cents !== undefined, "One or both of the amount_cents to be compared is undefined");
    var _a_amount_cents, _b_amount_cents;
    return ((_a_amount_cents = a.amount_cents) !== null && _a_amount_cents !== void 0 ? _a_amount_cents : 0) >= ((_b_amount_cents = b.amount_cents) !== null && _b_amount_cents !== void 0 ? _b_amount_cents : 0);
};
MoneyMath.greaterOrEqualToZero = function(money) {
    return MoneyMath.greaterOrEqual(money, moneyOrZero(undefined, money.currency));
};
